/**
 * Authentication error handling utilities for the Aurumrun application
 */

/**
 * Authentication error codes
 */
export enum AuthErrorCode {
  TOKEN_INVALID = 'TOKEN_INVALID',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMITED = 'RATE_LIMITED',
  WALLET_REJECTED = 'WALLET_REJECTED',
  ACCOUNT_BANNED = 'ACCOUNT_BANNED',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Authentication error structure
 */
export interface AuthError {
  code: AuthErrorCode
  message: string
  /**
   * i18n key for the copy we would show — which for a mapped Supabase code may
   * be MORE specific than `message` (an `otp_expired` with no server sentence
   * carries the generic UNKNOWN English in `message` and the precise
   * "code is wrong or expired" key here).
   *
   * Absent whenever `message` came from the server — a ban reason, an
   * edge-function validation message — because those are already the most
   * specific thing we can show and have no key to look up. Callers that have a
   * `t()` render this key and fall back to `message`; callers that don't (and
   * the tests) keep getting the English copy they always did.
   */
  messageKey?: string
  retry?: boolean
  bannedUntil?: string | null
}

/**
 * User-friendly error messages for each error code
 */
const ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  [AuthErrorCode.TOKEN_INVALID]: 'Your session is invalid. Please sign in again.',
  [AuthErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please sign in again.',
  [AuthErrorCode.NETWORK_ERROR]: 'Unable to connect to the server. Please check your internet connection.',
  [AuthErrorCode.RATE_LIMITED]: 'Too many requests. Please wait a moment and try again.',
  [AuthErrorCode.WALLET_REJECTED]: 'Wallet connection was cancelled. Please try again when ready.',
  [AuthErrorCode.ACCOUNT_BANNED]: 'Your account has been banned.',
  [AuthErrorCode.UNKNOWN]: 'An unexpected error occurred. Please try again.'
}

/** Translation key per code — kept 1:1 with ERROR_MESSAGES above. */
const ERROR_MESSAGE_KEYS: Record<AuthErrorCode, string> = {
  [AuthErrorCode.TOKEN_INVALID]: 'errors.auth.sessionInvalid',
  [AuthErrorCode.SESSION_EXPIRED]: 'errors.auth.sessionExpired',
  [AuthErrorCode.NETWORK_ERROR]: 'errors.auth.networkError',
  [AuthErrorCode.RATE_LIMITED]: 'errors.auth.rateLimited',
  [AuthErrorCode.WALLET_REJECTED]: 'errors.auth.walletRejected',
  [AuthErrorCode.ACCOUNT_BANNED]: 'errors.auth.accountBanned',
  [AuthErrorCode.UNKNOWN]: 'errors.auth.unexpected'
}

/**
 * Supabase Auth `error_code` values worth their own copy.
 *
 * These carry an English `.message` straight from GoTrue ("Email rate limit
 * exceeded", "Token has expired or is invalid"), which used to be rendered
 * verbatim to every player regardless of locale — and, for the two 429s, was
 * swallowed by the generic status-code branch below instead. Matching on the
 * stable machine-readable code lets us say something both translated and more
 * specific than "Too many requests".
 *
 * Codes not listed here still fall through to the status/`UNKNOWN` handling,
 * which surfaces GoTrue's own message — worse than a translation, better than
 * nothing.
 */
const SUPABASE_ERROR_CODES: Record<string, { code: AuthErrorCode; key: string; retry: boolean }> = {
  // Per-address send cooldown AND the project-wide hourly email budget both
  // report this one. See [auth.email] max_frequency / [auth.rate_limit]
  // email_sent in apps/supabase/supabase/config.toml.
  over_email_send_rate_limit: { code: AuthErrorCode.RATE_LIMITED, key: 'errors.auth.emailSendRateLimit', retry: true },
  over_sms_send_rate_limit: { code: AuthErrorCode.RATE_LIMITED, key: 'errors.auth.rateLimited', retry: true },
  over_request_rate_limit: { code: AuthErrorCode.RATE_LIMITED, key: 'errors.auth.rateLimited', retry: true },
  // Wrong or stale six-digit code — the most-seen error in the OTP step.
  otp_expired: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.otpExpired', retry: true },
  otp_disabled: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.emailSignInUnavailable', retry: false },
  email_provider_disabled: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.emailSignInUnavailable', retry: false },
  signup_disabled: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.signupDisabled', retry: false },
  email_address_invalid: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.invalidEmail', retry: false },
  // Generic, NOT email-specific: GoTrue raises `validation_failed` for any
  // request that misses its schema — a bad `type` on verifyOtp, a missing
  // param, an unknown provider. `parseAuthError` is shared by the wallet-link
  // and OAuth flows too, so keying it to the email copy would tell someone on
  // a screen with no email field to check their email address.
  validation_failed: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.unexpected', retry: false },
  email_exists: { code: AuthErrorCode.UNKNOWN, key: 'errors.auth.alreadyRegistered', retry: false }
  // Deliberately NOT listed: `user_banned`. Bans live in `public.users`, not in
  // GoTrue's `banned_until`, and our ban response is the 403 `reason` branch
  // below — which also carries `bannedUntil`. Matching a code here first would
  // shadow it and drop that field.
}

/**
 * Check if an error is a network error
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('net::') ||
      message.includes('timeout') ||
      message.includes('econnrefused')
    )
  }

  return false
}

/**
 * Check if an error indicates wallet rejection
 */
function isWalletRejected(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return message.includes('rejected') || message.includes('denied')
  }

  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>
    const message = String(errorObj.message || '').toLowerCase()
    return message.includes('rejected') || message.includes('denied')
  }

  return false
}

/**
 * Extract HTTP status code from error
 */
function getStatusCode(error: unknown): number | null {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>

    // Check for status directly on error
    if (typeof errorObj.status === 'number') {
      return errorObj.status
    }

    // Check for statusCode (Nuxt/h3 style)
    if (typeof errorObj.statusCode === 'number') {
      return errorObj.statusCode
    }

    // Check for response.status (fetch style)
    if (
      typeof errorObj.response === 'object' &&
      errorObj.response !== null &&
      typeof (errorObj.response as Record<string, unknown>).status === 'number'
    ) {
      return (errorObj.response as Record<string, unknown>).status as number
    }
  }

  return null
}

/**
 * Extract the JSON response body from a $fetch/ofetch error.
 * $fetch errors expose the parsed body on `.data`; Nuxt/h3 errors on `.response._data`.
 */
function getResponseData(error: unknown): Record<string, unknown> | null {
  if (typeof error !== 'object' || error === null) return null
  const errorObj = error as Record<string, unknown>

  if (typeof errorObj.data === 'object' && errorObj.data !== null) {
    return errorObj.data as Record<string, unknown>
  }

  const response = errorObj.response as Record<string, unknown> | undefined
  if (response && typeof response._data === 'object' && response._data !== null) {
    return response._data as Record<string, unknown>
  }

  return null
}

/** supabase-js tags every AuthApiError / AuthRetryableFetchError with this flag. */
function isSupabaseAuthError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null
    && (error as { __isAuthError?: unknown }).__isAuthError === true
  )
}

/**
 * The machine-readable Supabase Auth error code (`otp_expired`,
 * `over_email_send_rate_limit`, …), when the error is one of theirs.
 *
 * Read off `.code` — supabase-js's own field — with a fallback to the
 * `error_code` GoTrue puts in the JSON body, which is what survives when an
 * error is re-thrown through a plain fetch wrapper instead of the SDK.
 */
function getSupabaseErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const errorObj = error as Record<string, unknown>

  if (isSupabaseAuthError(error) && typeof errorObj.code === 'string' && errorObj.code.length > 0) {
    return errorObj.code
  }

  const data = getResponseData(error)
  for (const field of ['error_code', 'code'] as const) {
    const value = data?.[field]
    if (typeof value === 'string' && value.length > 0) return value
  }

  return null
}

/**
 * Extract the server-provided user-facing message from an error, if one exists.
 *
 * Supabase Auth errors (AuthApiError, AuthRetryableFetchError, ...) carry a
 * user-friendly `.message` and are tagged with `__isAuthError`. Edge-function
 * validation errors put the message in the response body, which $fetch/ofetch
 * surfaces on `error.data.message` (or `error.response._data.message`).
 *
 * Returns null when there's no specific message to show — callers fall back
 * to the generic UNKNOWN message in that case.
 */
function getServerErrorMessage(error: unknown): string | null {
  if (
    isSupabaseAuthError(error)
    && typeof (error as { message?: unknown }).message === 'string'
    && (error as { message: string }).message.length > 0
  ) {
    return (error as { message: string }).message
  }

  const data = getResponseData(error)
  if (data && typeof data.message === 'string' && data.message.length > 0) {
    return data.message
  }

  return null
}

/**
 * Parse an unknown error into a structured AuthError
 * @param error - The error to parse
 * @returns Structured AuthError with code, message, and retry hint
 */
export function parseAuthError(error: unknown): AuthError {
  // Check for wallet rejection first (specific user action)
  if (isWalletRejected(error)) {
    return {
      code: AuthErrorCode.WALLET_REJECTED,
      message: ERROR_MESSAGES[AuthErrorCode.WALLET_REJECTED],
      messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.WALLET_REJECTED],
      retry: true
    }
  }

  // Check for network errors
  if (isNetworkError(error)) {
    return {
      code: AuthErrorCode.NETWORK_ERROR,
      message: ERROR_MESSAGES[AuthErrorCode.NETWORK_ERROR],
      messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.NETWORK_ERROR],
      retry: true
    }
  }

  // Named Supabase Auth codes before the status-code buckets below: both OTP
  // rate limits are 429s, and the generic "Too many requests" they used to
  // collapse into told the user nothing about which wait applied to them.
  const supabaseCode = getSupabaseErrorCode(error)
  const known = supabaseCode ? SUPABASE_ERROR_CODES[supabaseCode] : undefined
  if (known) {
    return {
      code: known.code,
      // Kept as GoTrue's own English message rather than our generic copy:
      // callers without a `t()` are better off with the specific sentence.
      message: getServerErrorMessage(error) ?? ERROR_MESSAGES[known.code],
      messageKey: known.key,
      retry: known.retry
    }
  }

  // Check for HTTP status codes
  const statusCode = getStatusCode(error)
  if (statusCode !== null) {
    if (statusCode === 401) {
      return {
        code: AuthErrorCode.TOKEN_INVALID,
        message: ERROR_MESSAGES[AuthErrorCode.TOKEN_INVALID],
        messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.TOKEN_INVALID],
        retry: false
      }
    }

    if (statusCode === 403) {
      const data = getResponseData(error)
      if (data && data.reason === 'user_banned') {
        const serverReason = typeof data.message === 'string' && data.message.length > 0
          ? data.message
          : null
        return {
          code: AuthErrorCode.ACCOUNT_BANNED,
          message: serverReason ?? ERROR_MESSAGES[AuthErrorCode.ACCOUNT_BANNED],
          // No key when the server named the reason — that text is the whole
          // point of the response and a translation would replace it with
          // something vaguer.
          ...(serverReason ? {} : { messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.ACCOUNT_BANNED] }),
          retry: false,
          bannedUntil: typeof data.bannedUntil === 'string' ? data.bannedUntil : null
        }
      }
    }

    if (statusCode === 429) {
      return {
        code: AuthErrorCode.RATE_LIMITED,
        message: ERROR_MESSAGES[AuthErrorCode.RATE_LIMITED],
        messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.RATE_LIMITED],
        retry: true
      }
    }
  }

  // Fallback to unknown error — surface the server-provided message if any
  // (unmapped Supabase Auth codes, edge-function validation errors like
  // `plus_alias_not_allowed`), otherwise fall back to the generic copy. Only
  // the generic branch is translatable; a server message has no key.
  const serverMessage = getServerErrorMessage(error)
  return {
    code: AuthErrorCode.UNKNOWN,
    message: serverMessage ?? ERROR_MESSAGES[AuthErrorCode.UNKNOWN],
    ...(serverMessage ? {} : { messageKey: ERROR_MESSAGE_KEYS[AuthErrorCode.UNKNOWN] }),
    retry: true
  }
}
